import { useApiAction } from "api/drops/useApiAction";
import { userListTelephoneApi } from "api/puzzel/puzzel";
import { useFeatureFlag } from "app/FeatureFlags/useFeatureFlags";
import { PUZZEL_OIDC_LOGIN_FLAG } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  getPuzzelUserId,
  getPuzzelUsername,
} from "app/PuzzelAuth/puzzelTokenStorage";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import { UserDetailsAndLogOnOffProvider } from "features/Header/PuzzelTelephone/provider/contexts/UserDetailsAndLogOnOffContext";
import { useSetUserValidation } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import { CallProvider } from "features/Header/PuzzelTelephone/provider/providers/CallStatusProvider";
import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SkeletonButton } from "shared/components/feedback/SkeletonLoader/SkeletonLoaderStyles";
import { DropsButton } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { useUserInfo } from "shared/hooks/useUserInfo";
import styled from "styled-components";

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-right: 2.5rem;
`;

type Props = {
  children: ReactNode;
};
const ValidateUserAndPresentLogon = (props: Props) => {
  const setUserId = useSetUserValidation();
  const { isAuthenticated, login } = usePuzzelAuthenticationContext();
  const { t } = useTranslation();
  const { enabled: oidcEnabled, isLoading: flagLoading } = useFeatureFlag(
    PUZZEL_OIDC_LOGIN_FLAG,
  );

  // Legacy: matches the Puzzel user list against the logged-in Vy username.
  // TODO: remove this lookup (and the puzzelOidcLogin flag/branch) once OIDC has rolled out
  // fully - the JWT claim lookup below is both simpler and not reliant on username matching.
  const { username } = useUserInfo();
  const [userListRequest, performGetUserList] = useApiAction(
    userListTelephoneApi,
    false,
  );

  useEffect(() => {
    if (!oidcEnabled && isAuthenticated) {
      performGetUserList();
    }
  }, [oidcEnabled, isAuthenticated]);

  const legacyUserId = userListRequest.data?.result?.find(
    (it: any) => it.userName === username,
  )?.id;

  // OIDC: agent user id/username come straight from the access token - no more fragile
  // username matching.
  const oidcUserId = oidcEnabled && isAuthenticated ? getPuzzelUserId() : null;

  const puzzelUserId = oidcEnabled ? oidcUserId : legacyUserId;

  useEffect(() => {
    if (puzzelUserId) {
      setUserId({ userId: puzzelUserId });
      if (!oidcEnabled) {
        localStorage.setItem("puzzelUserId", puzzelUserId);
      }
    } else {
      setUserId({ userId: null });
      if (!oidcEnabled) {
        localStorage.removeItem("puzzelUserId");
      }
    }
  }, [puzzelUserId, oidcEnabled]);

  // Avoid flashing the "not registered"/"not logged in" branches below while the flag
  // value (and, once known, the OIDC auth check) is still in flight.
  if (flagLoading) {
    return <SkeletonButton />;
  }

  // OIDC-only: Puzzel login should only gate calling features, not the rest of the dashboard.
  // Legacy's silent login never reaches this (isAuthenticated becomes true automatically).
  if (oidcEnabled && !isAuthenticated) {
    return (
      <HorizontalContainer>
        <Text>{t("Ikke innlogget i Puzzel")}</Text>
        <DropsButton size="sm" variant="secondary" onClick={login}>
          {t("Logg inn i Puzzel")}
        </DropsButton>
      </HorizontalContainer>
    );
  }

  if (!puzzelUserId) {
    return (
      <Text
        style={{
          marginRight: "2.5rem",

          // Behaviour: Use max 3 lines and show "..." at overflow
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: "3",
          WebkitBoxOrient: "vertical",
        }}
      >
        {t(
          "Bruker ({{username}}) er ikke registrert i Puzzel. Ta kontakt med admin",
          { username: oidcEnabled ? (getPuzzelUsername() ?? "") : username },
        )}
      </Text>
    );
  }

  const { children } = props;
  return (
    <UserDetailsAndLogOnOffProvider>
      <CallProvider>{children}</CallProvider>
    </UserDetailsAndLogOnOffProvider>
  );
};

export default ValidateUserAndPresentLogon;
